import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, AccountAvatar, SectionLabel } from '../../../components/tcn/ui-kit';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import AlertBell from '../../../components/tcn-modules/m7/AlertBell';
import TierTotalsHeader from '../../../components/tcn-modules/m3/TierTotalsHeader';
import TierTableDesktop from '../../../components/tcn-modules/m3/TierTableDesktop';
import TierScorecardRow from '../../../components/tcn-modules/m3/TierScorecardRow';
import TierMetricRow from '../../../components/tcn-modules/m3/TierMetricRow';
import BiggestGapCard from '../../../components/tcn-modules/m3/BiggestGapCard';
import SectionHeader from '../../../components/tcn-modules/shared/SectionHeader';
import { formatDiamonds } from '../../../components/tcn-modules/shared/format';
import useIsDesktop from '../../../hooks/useIsDesktop';

/** A3.2–A3.5 stacked metric block — big-title header + one TierMetricRow per tier. */
const MetricBlock = ({ title, subtitle, view, tiers }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <SectionHeader title={title} subtitle={subtitle} />
    {tiers.map((r) => (
      <TierMetricRow key={`${view}-${r.id || r.tier}`} view={view} row={r} />
    ))}
  </div>
);

const FOCUS_METRIC_LABELS = (metric, t) => {
  const keys = {
    diamonds_completion_pct_vs_monthly: t('tcn.m1.metric.diamonds', 'Monthly diamonds'),
    recruited_creators_completion_pct_vs_monthly: t('tcn.m1.metric.recruited', 'Recruited creators'),
    valid_go_live_rate: t('tcn.m1.metric.validGoLive', 'Valid go-LIVE rate'),
    rank_up_eligibility_rate_7d15h: t('tcn.m1.metric.rankUpEligibility', 'Rank-up eligibility (7D/15H)'),
    pk_penetration: t('tcn.m1.metric.pkPenetration', 'PK penetration'),
    t14_ratio: t('tcn.m1.metric.t14', 'T+14 ratio'),
    go_live_rate_15d_45h_first30: t('tcn.m1.metric.goLive15d45h', 'Go-LIVE 15d/45h (first 30)'),
    first_30d_pk_count_20_ratio: t('tcn.m1.metric.first30PK', 'First 30d PK ≥ 20'),
  };
  return keys[metric] || metric;
};

/**
 * /tcn/me/tiers — Module 3 — My Creator Tasks (By Tier).
 *
 * Single SELECT on `manager_tier_current_month` filtered by manager_id = auth.uid().
 * Returns up to 11 rows (Tier 1..10 + Totals row, tier=0).
 *
 * Mobile  → totals header + biggest_gap callout + vertical stack of TierRow cards
 *           (only tiers with creators_in_tier > 0 are shown by default;
 *           empty tiers collapsed under a "Show empty tiers" toggle)
 * Desktop → totals header + biggest_gap callout + 11-row table (TierTableDesktop)
 *
 * Targets / completion %s are NULL until Task #33 backfills the recompute
 * (depends on tenant_objective_rules of type upgrade_pct_of_tier — D-038).
 */
const TcnTiersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusedMetric = searchParams.get('gap');
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [rows, setRows] = useState([]);
  const [thresholds, setThresholds] = useState({}); // tier -> min_diamonds (for A3.1 label)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    const [{ data, error: err }, { data: rk }] = await Promise.all([
      supabase
        .from('manager_tier_current_month')
        .select('*')
        .eq('manager_id', user.id)
        .order('tier', { ascending: true }),
      // Tier diamond thresholds → A3.1 label "Tier N — {threshold}" (MENA, R7).
      supabase.from('rankup_config').select('tier, min_diamonds').eq('server', 'MENA'),
    ]);
    if (err) setError(err);
    else setRows(data || []);
    if (rk) setThresholds(Object.fromEntries(rk.map((r) => [r.tier, Number(r.min_diamonds)])));
    setLoading(false);
  }, [user?.id]);

  // A3.1 label: "Tier 6 — 700K" (omit threshold for T1 = 0).
  const tierLabel = useCallback(
    (tier) => {
      const min = thresholds[tier];
      return min ? `Tier ${tier} — ${formatDiamonds(min)}` : `Tier ${tier}`;
    },
    [thresholds]
  );

  useEffect(() => { load(); }, [load]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const totals = rows.find(r => r.is_totals_row || r.tier === 0);
  const tierRows = rows
    .filter(r => !(r.is_totals_row || r.tier === 0))
    .sort((a, b) => a.tier - b.tier);
  const visibleTiers = showEmpty
    ? tierRows
    : tierRows.filter(r => (r.creators_in_tier ?? 0) > 0);
  const hiddenCount = tierRows.length - visibleTiers.length;

  // A3.6 — biggest completion gap (focus override from M1 CTA, else totals.biggest_gap).
  const effectiveGap = totals
    ? (focusedMetric
        ? {
            metric: focusedMetric,
            label: t('tcn.m3.focus.label', 'Focus: {{metric}}', {
              metric: FOCUS_METRIC_LABELS(focusedMetric, t),
            }),
          }
        : (totals.biggest_gap && Object.keys(totals.biggest_gap).length > 0
            ? totals.biggest_gap
            : null))
    : null;

  // A3.6/A3.7 — drill a tier row into Module 4 filtered to that tier.
  const drillTier = (tier) => navigate(`/tcn/me/creators?tier=${tier}`);

  return (
    <TcnApp
      tab="tiers"
      onTabChange={handleTabChange}
      desktopUserName={userName}
      desktopBreadcrumb={[
        t('tcn.m1.breadcrumb.dashboard', 'Dashboard'),
        t('tcn.tabs.tiers', 'Tiers'),
      ]}
      mobileHeader={
        <TopBar
          leading={<AccountAvatar name={userName} size={36} ring />}
          trailing={<AlertBell />}
          title={t('tcn.m3.title', 'My tiers')}
          subtitle={t('tcn.m3.subtitle', 'Tier scorecard')}
          largeTitle
        />
      }
    >
      <div
        style={{
          maxWidth: isDesktop ? 1200 : '100%',
          padding: isDesktop ? 0 : '0 16px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {loading && (
          <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 13 }}>
            {t('tcn.common.loading', 'Loading…')}
          </Card>
        )}

        {error && (
          <Card padded style={{ padding: 16, color: TCN.red, fontSize: 13 }}>
            {error.message || t('tcn.common.error', 'Something went wrong.')}
          </Card>
        )}

        {!loading && !error && !totals && (
          <Card padded style={{ padding: 16, color: TCN.textDim, fontSize: 13 }}>
            {t('tcn.m3.empty', 'No tier data yet. Run an injection to populate this view.')}
          </Card>
        )}

        {totals && (
          isDesktop ? (
            <>
              <TierTotalsHeader totals={totals} isDesktop={isDesktop} />
              {effectiveGap ? <BiggestGapCard biggestGap={effectiveGap} /> : null}
              <SectionLabel>{t('tcn.m3.section.byTier', 'Tier breakdown')}</SectionLabel>
              <TierTableDesktop rows={rows} />
            </>
          ) : (
            /* Mobile — one scrollable screen: biggest gap (A3.6) + stacked big-title
               blocks A3.1 Scorecard → A3.2 Diamonds → A3.3 Upgrades → A3.4 Maintains
               → A3.5 Process (mockup artboards stacked, no internal tabs). */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {effectiveGap ? <BiggestGapCard biggestGap={effectiveGap} /> : null}

              {/* A3.1 — Tier scorecard + Totals */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SectionHeader
                  title={t('tcn.m3.scorecard.title', 'Tier scorecard')}
                  subtitle={t('tcn.m3.scorecard.subtitle', 'Tier 1 → Tier 10 + Totals')}
                />
                {visibleTiers.map((r) => (
                  <TierScorecardRow
                    key={r.id || `tier-${r.tier}`}
                    tier={r.tier}
                    label={tierLabel(r.tier)}
                    count={r.creators_in_tier ?? 0}
                    diamondsMtd={r.diamonds_mtd_tier_clear ?? 0}
                    completionPct={r.diamonds_completion_tier_pct ?? null}
                    onClick={(r.creators_in_tier ?? 0) > 0 ? drillTier : undefined}
                  />
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowEmpty((v) => !v)}
                    style={{
                      width: '100%', padding: '12px 16px', background: 'transparent', color: TCN.cyan,
                      border: `1px dashed ${TCN.border}`, borderRadius: 12, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {showEmpty
                      ? t('tcn.m3.hideEmpty', 'Hide empty tiers')
                      : t('tcn.m3.showEmpty', 'Show {{count}} empty tiers', { count: hiddenCount })}
                  </button>
                )}
                <div style={{ marginTop: 4 }}>
                  <TierScorecardRow
                    isTotals
                    count={totals.creators_in_tier ?? 0}
                    diamondsMtd={totals.diamonds_mtd_tier_clear ?? 0}
                    completionPct={totals.diamonds_completion_tier_pct ?? null}
                  />
                </div>
              </div>

              {/* A3.2 — Diamonds by tier */}
              <MetricBlock
                title={t('tcn.m3.diamonds.title', 'Diamonds by tier')}
                subtitle={t('tcn.m3.diamonds.subtitle', 'MTD · target · last month')}
                view="diamonds"
                tiers={visibleTiers}
              />
              {/* A3.3 — Upgrades */}
              <MetricBlock
                title={t('tcn.m3.upgrades.title', 'Upgrades')}
                subtitle={t('tcn.m3.upgrades.subtitle', 'Creators upgraded this month')}
                view="upgrades"
                tiers={visibleTiers}
              />
              {/* A3.4 — Maintains */}
              <MetricBlock
                title={t('tcn.m3.maintains.title', 'Maintains')}
                subtitle={t('tcn.m3.maintains.subtitle', 'Creators held at tier')}
                view="maintains"
                tiers={visibleTiers}
              />
              {/* A3.5 — Process metrics */}
              <MetricBlock
                title={t('tcn.m3.process.title', 'Process metrics')}
                subtitle={t('tcn.m3.process.subtitle', 'Go-LIVE rate · PK penetration')}
                view="process"
                tiers={visibleTiers}
              />
            </div>
          )
        )}
      </div>
    </TcnApp>
  );
};

export default TcnTiersPage;
